import { View } from 'react-native'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import InputsSection from '@ui/forms/InputsSection'
import FormInput from '@ui/forms/Input/FormInput'
import Slider from '@ui/components/Slider'
import { useForm, useWatch } from 'react-hook-form'
import { NumberUtils } from '@ca/common'
import Page from '@shell/Page'
import ButtonsFixed from '@ui/buttons/ButtonsFixed'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import Input from '@ui/forms/Input'

const min = 207.5
const max = 415.0

export default function InputControlled() {
  const { t } = useTranslation(['modals', 'ui', 'types', 'payment'])
  const text = 'payment:settings'

  const isCustomAmountDisabled = false
  const [inputText, setInputText] = useState('')

  const form = useForm<{ amount: string }, any, { amount: string }>({
    defaultValues: {
      amount: `${min}`,
    },
    // react-hook-form ≥7.55: zodResolver infers z.input<schema> (amount: unknown via .transform())
    // as TFieldValues, mismatching { amount: string }. `as any` preserves pre-7.55 behavior —
    // Zod still validates and transforms at runtime. See docs/expo54-migration.md § Known Issues.
    resolver: zodResolver(
      z
        .object({
          amount: z
            .string()
            .transform(NumberUtils.parse.toFloatOrNull)
            .refine((val) => val !== null, { message: 'amount.invalid' })
            .refine((val) => Number.isFinite(val), {
              message: 'amount.invalid',
            }),
        })
        .superRefine((data, ctx) => {
          const amount = data.amount ?? 0
          if (amount < min) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'amount.min',
              path: ['amount'],
            })
          }
          if (amount > max) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'amount.max',
              path: ['amount'],
            })
          }
        })
    ) as any,
  })

  const amount = useWatch({ name: 'amount', control: form.control })

  const onSubmit = form.handleSubmit((data) => {
    console.log(data)
  })

  return (
    <Page stage="dev">
      <View className="gap-8 p-8">
        <InputsSection
          title={t(`${text}.amount.title`)}
          desc={t(
            `${text}.amount.${isCustomAmountDisabled ? 'descFull' : 'desc'}`
          )}
        >
          <FormInput
            name="amount"
            control={form.control}
            label={t(`${text}.amount.label`)}
            disabled={isCustomAmountDisabled}
          />
          <Slider
            min={min}
            max={max}
            disabled={isCustomAmountDisabled}
            minPercentage={50}
            maxPercentage={100}
            step={1}
            // value={parse(amount || '0')}
            value={NumberUtils.parse.toFloat(amount || '0')}
            onChange={(value: number) => {
              form.setValue('amount', `${value.toFixed(2)}`, {
                shouldValidate: true,
              })
            }}
          />
        </InputsSection>
        <Input
          label="Success"
          placeholder="Placeholder"
          icon="info-circle"
          successMessage={inputText}
          onChangeText={(text) => setInputText(text)}
        />
        <Input
          label="Error"
          placeholder="Placeholder"
          icon="info-circle"
          errorMessage={inputText}
          onChangeText={(text) => setInputText(text)}
        />
      </View>
      <ButtonsFixed
        buttons={[
          {
            variant: 'filled',
            label: t('ui:submit'),
            iconEnd: 'arrow-narrow-right',
            onPress: onSubmit,
          },
        ]}
      />
    </Page>
  )
}
